import ArrowIcon from '@mui/icons-material/ArrowDropDown';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import React, { useState } from 'react';

import { AgentStatusMenuProps } from './AgentStatusMenu.props';
import { AgentStatusDot } from '../../atoms/AgentStatusDot';

// Drop-down picker for agent availability state. System-managed states
// (set by AC during call legs) are filtered out — the agent shouldn't be
// able to flip into them manually. We filter on the raw AC AgentStateType
// rather than the visual `kind` because custom break / lunch states come
// back as `not_routable` and share the `busy` visual classification, but
// are legitimate user-selectable choices.

export const AgentStatusMenu: React.FC<AgentStatusMenuProps> = ({
  current,
  options,
  onSelect,
  disabled = false,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);

  const open = (e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const close = () => setAnchor(null);

  const handleSelect = async (name: string) => {
    close();
    setBusy(true);
    try {
      await onSelect(name);
    } finally {
      setBusy(false);
    }
  };

  // Hide AC system-typed states (set during call legs, not user-selectable)
  // and any state name containing "RX" — these are routing-helper states
  // that aren't meant for manual selection. Sort alphabetically so the menu
  // is predictable regardless of the order Connect returned them in.
  const selectable = options
    .filter((o) => o.type !== 'system' && !/RX/i.test(o.name))
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
  const label = current?.name ?? 'Offline';
  const kind = current?.kind ?? 'default';

  return (
    <Box>
      <Button
        onClick={open}
        disabled={disabled || busy}
        size="small"
        endIcon={<ArrowIcon />}
        startIcon={<AgentStatusDot kind={kind} />}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        sx={{
          textTransform: 'none',
          color: 'text.primary',
          borderColor: 'v4.border',
          '&:hover': { bgcolor: 'v4.tableHover' },
        }}
        variant="outlined"
      >
        {label}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {selectable.length === 0 ? (
          <MenuItem disabled>No states available</MenuItem>
        ) : (
          selectable.map((opt) => (
            <MenuItem
              key={opt.name}
              selected={opt.name === current?.name}
              onClick={() => handleSelect(opt.name)}
            >
              <AgentStatusDot kind={opt.kind} />
              <Box component="span" sx={{ ml: 1 }}>
                {opt.name}
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  );
};
